import React from 'react';
import { Alert, Box, Button, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import { useNotificationContext } from '../contexts/NotificationProvider';
import { FCM_BLOCK_REASON, getFcmBlockReasonMessage } from '../services/fcmWebRegistration';

const BANNER_Z_INDEX = 1400;

const NotificationAlertsBar = ({ title, body, severity, icon, onDismiss, primaryAction }) => (
  <Box
    role="region"
    aria-label="Notification alerts"
    sx={{
      position: 'fixed',
      bottom: 0,
      left: 0,
      right: 0,
      zIndex: BANNER_Z_INDEX,
      px: { xs: 1.5, sm: 2 },
      pb: { xs: 1.5, sm: 2 },
      pt: 0,
      pointerEvents: 'none',
    }}
  >
    <Alert
      severity={severity}
      icon={icon}
      sx={{
        pointerEvents: 'auto',
        alignItems: { xs: 'flex-start', sm: 'center' },
        width: '100%',
        maxWidth: 720,
        mx: 'auto',
        borderRadius: 2,
        boxShadow: '0 -4px 24px rgba(0,0,0,0.08)',
        '& .MuiAlert-message': { width: '100%', py: 0.25 },
      }}
      action={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
          {primaryAction ? (
            <Button
              color="inherit"
              size="small"
              variant="outlined"
              onClick={primaryAction.onClick}
              disabled={primaryAction.loading}
              sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}
            >
              {primaryAction.loading ? 'Connecting…' : primaryAction.label}
            </Button>
          ) : null}
          <IconButton
            size="small"
            onClick={onDismiss}
            aria-label="Dismiss for now"
            sx={{ color: 'inherit' }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      }
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.35, mb: 0.25 }}>
        {title}
      </Typography>
      <Typography variant="body2" sx={{ lineHeight: 1.45, opacity: 0.92 }}>
        {body}
      </Typography>
    </Alert>
  </Box>
);

/**
 * Bottom bar for push setup. Dismiss hides it for the session — use the header bell menu to
 * enable notifications again without signing out.
 */
const NotificationPermissionBanner = () => {
  const { permission, alertsSetup } = useNotificationContext() ?? {};

  if (!alertsSetup?.showBanner) {
    return null;
  }

  const { states, isBusy, handleDismiss, handleEnableAlerts, handleRetryPush } = alertsSetup;

  const {
    showDenied,
    showConfigIssue,
    showIosSafariPwa,
    showUnsupported,
    showInsecure,
    showRegistrationRetry,
    needsPushSetup,
    blockReason,
    blockReasonMessage,
  } = states;

  if (needsPushSetup) {
    const isReconnect = permission === 'granted';
    return (
      <NotificationAlertsBar
        icon={<NotificationsActiveIcon fontSize="inherit" />}
        title={isReconnect ? 'Finish notification setup' : 'Enable notifications'}
        body={
          isReconnect
            ? 'Notifications are allowed in your browser, but this device is not connected yet. Connect push for background updates.'
            : 'Get event and community updates when this tab is in the background or minimized.'
        }
        severity="info"
        onDismiss={handleDismiss}
        primaryAction={{
          label: isReconnect ? 'Connect' : 'Enable',
          onClick: handleEnableAlerts,
          loading: isBusy,
        }}
      />
    );
  }

  if (showRegistrationRetry) {
    return (
      <NotificationAlertsBar
        icon={<NotificationsActiveIcon fontSize="inherit" />}
        title="Could not connect push"
        body={
          blockReasonMessage ||
          getFcmBlockReasonMessage(blockReason ?? FCM_BLOCK_REASON.PUSH_SERVICE_UNAVAILABLE)
        }
        severity="warning"
        onDismiss={handleDismiss}
        primaryAction={{
          label: 'Retry',
          onClick: handleRetryPush,
          loading: isBusy,
        }}
      />
    );
  }

  if (showDenied) {
    return (
      <NotificationAlertsBar
        icon={<InfoOutlinedIcon fontSize="inherit" />}
        title="Notifications blocked"
        body={getFcmBlockReasonMessage(FCM_BLOCK_REASON.PERMISSION_DENIED)}
        severity="warning"
        onDismiss={handleDismiss}
      />
    );
  }

  const body =
    blockReasonMessage ||
    getFcmBlockReasonMessage(
      showConfigIssue
        ? FCM_BLOCK_REASON.MISSING_VAPID
        : showInsecure
          ? FCM_BLOCK_REASON.INSECURE_CONTEXT
          : showIosSafariPwa
            ? FCM_BLOCK_REASON.SAFARI_IOS_PWA_REQUIRED
            : showUnsupported
              ? FCM_BLOCK_REASON.UNSUPPORTED_BROWSER
              : FCM_BLOCK_REASON.UNSUPPORTED_BROWSER,
    );

  return (
    <NotificationAlertsBar
      icon={<InfoOutlinedIcon fontSize="inherit" />}
      title="Notifications unavailable"
      body={body}
      severity="warning"
      onDismiss={handleDismiss}
    />
  );
};

export default NotificationPermissionBanner;
